// src/components/workspace/AiEditReviewDialog.tsx
// Approval gate for AI-proposed section edits. The AI Tools (Chat "Apply", Gap
// "Generate/Reframe") draft a replacement for a section, but NOTHING is written
// to the proposal until the user reviews the proposed text here and clicks Apply.
// While drafting, a spinner shows; on result, the user can toggle between the
// proposed version and the current one before approving or discarding.

import React, { useState } from 'react';
import { X, Check, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { jsonToReadableText } from '../../services/ProposalExportService';

export interface AiEditState {
  status: 'drafting' | 'review';
  sectionType: string;
  sectionTitle: string;
  instruction: string;
  source: 'chat' | 'gap';
  before?: string;
  after?: string;
  // When the edit was launched from a Coverage requirement, its id — so on Apply
  // we can re-check just that one requirement and flip its row live.
  coverageItemId?: string;
}

interface AiEditReviewDialogProps {
  edit: AiEditState | null;
  applying: boolean;
  onApply: () => void;
  onDiscard: () => void;
}

// Render a section's stored content (often a JSON string) as readable text.
function readable(content: string | undefined, title: string): string {
  if (!content) return '(empty)';
  try {
    return jsonToReadableText(content, title) || content;
  } catch {
    return content;
  }
}

export function AiEditReviewDialog({ edit, applying, onApply, onDiscard }: AiEditReviewDialogProps) {
  const [view, setView] = useState<'proposed' | 'current'>('proposed');
  if (!edit) return null;

  const drafting = edit.status === 'drafting';
  const proposedText = readable(edit.after, edit.sectionTitle);
  const currentText = readable(edit.before, edit.sectionTitle);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-bw-surface rounded-xl shadow-xl border border-bw-border w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-bw-border">
          <div className="flex items-start gap-2 min-w-0">
            <Sparkles className="h-5 w-5 text-bw-primary mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-bw-ink">Review proposed change</h3>
              <p className="text-xs text-bw-muted truncate">
                {edit.sectionTitle} · {edit.source === 'chat' ? 'from your chat request' : 'resolving a gap'}
              </p>
            </div>
          </div>
          <button onClick={onDiscard} disabled={applying} className="p-1 hover:bg-bw-surface-subtle rounded disabled:opacity-50">
            <X className="h-4 w-4 text-bw-muted" />
          </button>
        </div>

        {/* The instruction driving the change */}
        <div className="px-4 py-2.5 bg-bw-surface-subtle border-b border-bw-border">
          <p className="text-[11px] font-semibold text-bw-muted uppercase tracking-wide mb-0.5">Instruction</p>
          <p className="text-sm text-bw-ink whitespace-pre-wrap line-clamp-3">{edit.instruction}</p>
        </div>

        {/* Body */}
        {drafting ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-bw-primary" />
            <p className="text-sm text-bw-muted">Drafting a proposed change… nothing is saved yet.</p>
            <p className="text-xs text-bw-muted">This can take up to a couple of minutes for large sections.</p>
          </div>
        ) : (
          <>
            {/* Proposed / Current toggle */}
            <div className="flex items-center gap-1 px-4 pt-3">
              <button
                onClick={() => setView('proposed')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  view === 'proposed' ? 'bg-bw-primary text-white' : 'bg-bw-surface-subtle text-bw-muted hover:bg-bw-border/60'
                }`}
              >
                Proposed
              </button>
              <button
                onClick={() => setView('current')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  view === 'current' ? 'bg-bw-primary text-white' : 'bg-bw-surface-subtle text-bw-muted hover:bg-bw-border/60'
                }`}
              >
                Current
              </button>
              <span className="ml-auto text-[11px] text-bw-muted">
                {view === 'proposed' ? 'What the AI suggests' : 'What is in the proposal now'}
              </span>
            </div>

            <div className="flex-1 overflow-auto p-4">
              <pre className="whitespace-pre-wrap text-sm text-bw-ink font-sans leading-relaxed">
                {view === 'proposed' ? proposedText : currentText}
              </pre>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-bw-border">
          <p className="text-xs text-bw-muted flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-fit-moderate flex-shrink-0" />
            Nothing is saved until you click Apply.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onDiscard}
              disabled={applying}
              className="px-3 py-1.5 text-sm text-bw-muted hover:text-bw-ink disabled:opacity-50"
            >
              Discard
            </button>
            <button
              onClick={onApply}
              disabled={drafting || applying}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-success text-white rounded-md text-sm font-medium hover:brightness-110 disabled:opacity-50"
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {applying ? 'Applying…' : 'Apply change'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AiEditReviewDialog;
